/**
 * MeetingRunPage — Live meeting runner
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LinkedTodoCreator } from '@/features/os-todos';
import { useMeeting, useFinishMeeting, useCancelMeeting } from '../hooks/useMeetings';
import { useStaffUserMap } from '../hooks/useStaffUsers';

// =============================================================================
// Helpers
// =============================================================================

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Montreal',
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

// =============================================================================
// MeetingRunPage
// =============================================================================

export function MeetingRunPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: meeting, isLoading } = useMeeting(id);
  const userMap = useStaffUserMap();
  const finish = useFinishMeeting();
  const cancel = useCancelMeeting();

  async function handleFinish() {
    if (!id) return;
    await finish.mutateAsync(id);
    navigate('/os/meetings?tab=past');
  }

  async function handleCancel() {
    if (!id) return;
    await cancel.mutateAsync(id);
    navigate('/os/meetings');
  }

  if (isLoading || !meeting) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 rounded bg-muted animate-pulse" />
        ))}
      </div>
    );
  }

  const owner = meeting.owner_id ? userMap.get(meeting.owner_id) : null;
  const ownerName = owner
    ? [owner.firstname, owner.lastname].filter(Boolean).join(' ') || owner.email
    : null;

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Back + title */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/os/meetings')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold">{meeting.title}</h1>
          <p className="text-sm text-muted-foreground">
            {formatDate(meeting.scheduled_at)}
            {ownerName && ` · ${ownerName}`}
          </p>
        </div>
      </div>

      {/* Sections */}
      {meeting.sections.length > 0 && (
        <div className="border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-muted/40 border-b">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Agenda</p>
          </div>
          <ul className="divide-y divide-border">
            {meeting.sections.map((section) => (
              <li key={section.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm">{section.name}</span>
                {section.duration_planned > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {Math.round(section.duration_planned / 60)} min
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Commitments — capture To-Dos as they come up in the L10 */}
      <div className="border rounded-lg overflow-hidden">
        <div className="px-4 py-3 bg-muted/40 border-b">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Commitments (To-Dos)
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Every commitment captured here is a 7-day To-Do linked to this meeting.
          </p>
        </div>
        <div className="p-3">
          <LinkedTodoCreator
            sourceType="meeting"
            sourceId={meeting.id}
            teamId={meeting.team_id ?? null}
            createdFromMeetingId={meeting.id}
          />
        </div>
      </div>

      {/* End actions */}
      <div className="flex items-center gap-3 pt-2">
        <Button
          onClick={handleFinish}
          disabled={finish.isPending}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          <CheckCircle className="h-4 w-4 mr-1.5" />
          End Meeting
        </Button>
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={cancel.isPending}
          className="text-destructive border-destructive/40 hover:bg-destructive/10"
        >
          <XCircle className="h-4 w-4 mr-1.5" />
          Cancel Meeting
        </Button>
      </div>
    </div>
  );
}
